<!-- src/views/engineering/EngineeringProjectsView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0] font-sans selection:bg-[#20FCF1]/30 text-left">
    <FormHeader />

    <div class="p-4 sm:p-6 md:p-8 no-print text-left">
      <div class="max-w-7xl mx-auto pb-24 space-y-10">
        
        <!-- 1. PREMIUM COMMAND HEADER -->
        <div class="bg-[#0A1630] rounded-[40px] p-8 md:p-12 border-b-4 border-[#20FCF1] shadow-2xl relative overflow-hidden animate-fadeIn">
          <div class="absolute top-0 right-0 p-12 opacity-5 pointer-events-none">
            <BuildingOffice2Icon class="h-32 w-32 text-white" />
          </div>
          
          <div class="relative z-10 flex flex-col xl:flex-row xl:items-center justify-between gap-8">
            <div>
              <h1 class="text-3xl md:text-4xl font-black text-white uppercase tracking-tight leading-none">Projects Command</h1>
              <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-4">
                Active Worksites • Bridge References • Client Feedback Hub • Plant & Equipment Register
              </p>
            </div>
            
            <!-- SINGLE-LEVEL ACTION BUTTONS -->
            <div class="flex flex-wrap items-center gap-3">
              <button 
                @click="openGlobalClientFeedbackModal" 
                class="px-6 py-3.5 bg-white/10 border border-white/10 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-white/20 transition-all flex items-center gap-2 shadow-lg cursor-pointer"
              >
                <QrCodeIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Client Feedback QR</span>
              </button>

              <router-link to="/dashboard" class="bg-white/5 border border-white/10 text-white px-8 py-3.5 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-white/10 transition-all flex items-center gap-3 shadow-lg active:scale-95">
                <ArrowUturnLeftIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Return</span>
              </router-link>
            </div>
          </div>
        </div>

        <!-- 2. MODE SELECTOR TABS & SEARCH CONTROLS -->
        <div class="bg-white p-6 rounded-[32px] shadow-sm border border-slate-100 flex flex-col lg:flex-row items-center justify-between gap-6 animate-fadeIn" style="animation-delay: 0.05s">
          
          <!-- VIEW MODE SWITCHER TABS -->
          <div class="flex items-center gap-2 w-full lg:w-auto">
            <button 
              @click="activeRegistryTab = 'projects'"
              :class="activeRegistryTab === 'projects' ? 'bg-[#0A1630] text-[#20FCF1] border-[#0A1630]' : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'"
              class="px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest border transition-all cursor-pointer flex items-center gap-2"
            >
              <BuildingOffice2Icon class="w-4 h-4" /> <span>Project Portfolio</span>
            </button>
            <button 
              @click="activeRegistryTab = 'plant'"
              :class="activeRegistryTab === 'plant' ? 'bg-[#0A1630] text-[#20FCF1] border-[#0A1630]' : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'"
              class="px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest border transition-all cursor-pointer flex items-center gap-2"
            >
              <WrenchScrewdriverIcon class="w-4 h-4" /> <span>Plant & Equipment Hub</span>
            </button>
          </div>

          <!-- DYNAMIC FILTER / STATUS TABS OR SEARCH -->
          <div class="flex flex-wrap items-center gap-3 w-full lg:w-auto justify-end">
            <template v-if="activeRegistryTab === 'projects'">
              <div class="flex items-center gap-1.5 overflow-x-auto pb-1 lg:pb-0">
                <button 
                  v-for="tab in statusTabs" 
                  :key="tab"
                  @click="selectedStatus = tab"
                  :class="selectedStatus === tab ? 'bg-[#20FCF1] text-[#0A1630] border-[#20FCF1]' : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'"
                  class="px-4 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest border transition-all cursor-pointer whitespace-nowrap"
                >
                  {{ tab }}
                </button>
              </div>
            </template>
            <template v-else>
              <select v-model="plantStatusFilter" class="bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-[10px] font-black uppercase tracking-widest text-[#0A1630] outline-none cursor-pointer">
                <option value="all">All Plant Statuses</option>
                <option value="In Service">In Service</option>
                <option value="Quarantined">Quarantined</option>
              </select>
            </template>

            <div class="w-full sm:w-72 relative">
              <MagnifyingGlassIcon class="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
              <input 
                v-model="searchQuery" 
                type="text" 
                :placeholder="activeRegistryTab === 'projects' ? 'Search projects, bridge ref...' : 'Search asset ID, description...'" 
                class="w-full bg-slate-50 border border-slate-200 rounded-2xl pl-11 pr-4 py-3 text-xs font-bold text-[#0A1630] outline-none focus:ring-2 focus:ring-[#20FCF1]"
              />
            </div>
          </div>
        </div>

        <!-- 3A. VIEW MODE: PROJECTS PORTFOLIO -->
        <section v-if="activeRegistryTab === 'projects'" class="space-y-6 animate-fadeIn" style="animation-delay: 0.1s">
          <div class="flex items-center justify-between px-2">
            <div class="flex items-center gap-3">
              <div class="bg-[#0A1630] p-2 rounded-xl shadow-lg border-b-2 border-[#20FCF1]">
                <BuildingOffice2Icon class="h-5 w-5 text-[#20FCF1]" />
              </div>
              <h2 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">Project Portfolio Register</h2>
            </div>
            <span class="px-4 py-1.5 bg-[#EBFDF8] border border-[#20FCF1]/20 text-[#20FCF1] text-[9px] font-black rounded-full uppercase tracking-widest">
              {{ filteredProjects.length }} Registered
            </span>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="py-20 text-center bg-slate-50 rounded-[40px] border border-dashed border-slate-200 shadow-inner">
             <div class="animate-spin h-10 w-10 border-4 border-[#0A1630] border-t-[#20FCF1] rounded-full mx-auto mb-4"></div>
             <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest">Synchronising Projects...</p>
          </div>

          <!-- Empty State -->
          <div v-else-if="filteredProjects.length === 0" class="py-20 text-center bg-slate-50 rounded-[40px] border border-dashed border-slate-200 shadow-inner">
             <BuildingOffice2Icon class="h-12 w-12 text-slate-300 mx-auto mb-4" />
             <h3 class="text-lg font-black text-[#0A1630] uppercase tracking-tight leading-none">No Projects Found</h3>
             <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-2">No projects match your current filter criteria.</p>
          </div>

          <!-- Project Cards Grid -->
          <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div 
              v-for="project in filteredProjects" 
              :key="project.id"
              class="bg-white border border-slate-100 rounded-[32px] overflow-hidden shadow-sm hover:shadow-xl hover:border-[#20FCF1]/30 transition-all flex flex-col justify-between group"
            >
              <div>
                <!-- Cover Image / Banner -->
                <div class="relative h-48 bg-slate-100 overflow-hidden">
                  <img 
                    v-if="project.imageUrl" 
                    :src="project.imageUrl" 
                    class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                    alt="Project Cover" 
                  />
                  <div v-else class="w-full h-full flex items-center justify-center bg-[#0A1630]/5 text-slate-400">
                    <BuildingOffice2Icon class="w-12 h-12 opacity-40" />
                  </div>
                  
                  <!-- Status Badge -->
                  <div class="absolute top-4 right-4">
                    <span 
                      :class="{
                        'bg-emerald-500 text-white': project.status === 'Active',
                        'bg-amber-500 text-white': project.status === 'In Planning',
                        'bg-blue-600 text-white': project.status === 'Complete',
                        'bg-slate-600 text-white': project.status === 'On Hold'
                      }"
                      class="text-[8px] font-black px-3 py-1.5 rounded-full uppercase tracking-widest shadow-lg"
                    >
                      {{ project.status || 'Active' }}
                    </span>
                  </div>
                </div>

                <div class="p-6 space-y-4">
                  <h3 class="text-xl font-black text-[#0A1630] uppercase tracking-tight leading-tight group-hover:text-[#FD5E00] transition-colors">
                    {{ project.name }}
                  </h3>

                  <!-- Metadata -->
                  <div class="space-y-2 pt-2 border-t border-slate-100">
                    <div v-if="project.siteAddress" class="flex items-center gap-2 text-slate-500">
                      <MapPinIcon class="w-4 h-4 text-[#20FCF1] flex-shrink-0" />
                      <span class="text-[10px] font-bold uppercase tracking-wider truncate">{{ project.siteAddress }}</span>
                    </div>

                    <div class="flex flex-wrap items-center gap-3 text-slate-400 pt-1">
                      <span v-if="project.bridgeRef" class="text-[9px] font-black bg-slate-100 px-2.5 py-1 rounded-md uppercase tracking-widest">
                        Bridge: {{ project.bridgeRef }}
                      </span>
                      <span v-if="project.lineRef" class="text-[9px] font-black bg-slate-100 px-2.5 py-1 rounded-md uppercase tracking-widest">
                        Line: {{ project.lineRef }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Card Actions -->
              <div class="p-6 pt-0">
                <button 
                  @click="openProjectDetails(project)"
                  class="w-full bg-[#0A1630] text-white py-3.5 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-[#20FCF1] hover:text-[#0A1630] transition-all shadow-md active:scale-95 flex items-center justify-center gap-2 cursor-pointer border-none"
                >
                  <EyeIcon class="w-4 h-4 text-[#20FCF1] group-hover:text-[#0A1630]" />
                  <span>View Details & Client Feedback</span>
                </button>
              </div>
            </div>
          </div>
        </section>

        <!-- 3B. VIEW MODE: PLANT & EQUIPMENT HUB -->
        <section v-if="activeRegistryTab === 'plant'" class="space-y-6 animate-fadeIn" style="animation-delay: 0.1s">
          <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 px-2">
            <div class="flex items-center gap-3">
              <div class="bg-[#0A1630] p-2 rounded-xl shadow-lg border-b-2 border-[#20FCF1]">
                <WrenchScrewdriverIcon class="h-5 w-5 text-[#20FCF1]" />
              </div>
              <h2 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">Plant & Equipment Register (MSPRG009)</h2>
            </div>
            
            <div class="flex items-center gap-3">
              <router-link :to="{ name: 'SingleItemInspectionForm' }" class="px-5 py-2.5 bg-[#0A1630] text-[#20FCF1] rounded-xl font-black uppercase text-[9px] tracking-widest hover:bg-[#20FCF1] hover:text-[#0A1630] transition-all shadow-md flex items-center gap-2">
                <ClipboardDocumentCheckIcon class="h-4 w-4" /> <span>Single-Item Inspection (SS-PEI-1)</span>
              </router-link>
              <router-link :to="{ name: 'MultiItemInspectionForm' }" class="px-5 py-2.5 bg-white border border-slate-200 text-[#0A1630] rounded-xl font-black uppercase text-[9px] tracking-widest hover:bg-slate-50 transition-all shadow-sm flex items-center gap-2">
                <TableCellsIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Multi-Item Check (SS-PEI-2)</span>
              </router-link>
            </div>
          </div>

          <!-- Plant List Grid -->
          <div class="space-y-4">
            <div v-for="asset in filteredPlantAssets" :key="asset.assetId" class="bg-white border border-slate-100 rounded-[32px] shadow-sm p-6 flex flex-col md:flex-row items-center justify-between gap-6">
              <div class="flex items-center gap-6 w-full md:w-[35%]">
                <div class="h-14 w-14 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-center text-[#0A1630] font-black text-xs shrink-0">
                  {{ asset.assetId }}
                </div>
                <div>
                  <h3 class="text-base font-black text-[#0A1630] uppercase tracking-tight leading-tight">{{ asset.description }}</h3>
                  <p class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest mt-1">{{ asset.category || 'Plant' }} • Risk: {{ asset.risk || 'Low' }}</p>
                </div>
              </div>

              <div class="flex-grow grid grid-cols-2 sm:grid-cols-3 gap-4 text-left w-full md:w-auto">
                <div>
                  <p class="text-[8px] font-black text-slate-400 uppercase tracking-widest">Serial No.</p>
                  <p class="text-xs font-bold text-slate-700 uppercase mt-0.5">{{ asset.serialNo || 'N/A' }}</p>
                </div>
                <div>
                  <p class="text-[8px] font-black text-slate-400 uppercase tracking-widest">Next Due</p>
                  <p class="text-xs font-bold text-slate-700 uppercase mt-0.5">{{ asset.nextDue || 'N/A' }}</p>
                </div>
                <div>
                  <p class="text-[8px] font-black text-slate-400 uppercase tracking-widest">Status</p>
                  <span class="px-3 py-1 inline-flex text-[9px] font-black rounded-full uppercase tracking-widest mt-0.5" :class="(asset.status || '').toLowerCase() === 'quarantined' ? 'bg-rose-50 text-rose-700' : 'bg-emerald-50 text-emerald-700'">
                    {{ asset.status || 'In Service' }}
                  </span>
                </div>
              </div>

              <div class="shrink-0 w-full md:w-auto flex justify-end">
                <router-link :to="{ name: 'SingleItemInspectionForm', params: { assetId: asset.assetId }}" class="px-6 py-3 bg-[#0A1630] text-white rounded-xl font-black uppercase text-[9px] tracking-widest hover:bg-[#20FCF1] hover:text-[#0A1630] transition-all shadow-md flex items-center gap-2">
                  <ClipboardDocumentCheckIcon class="h-4 w-4" /> <span>Inspect Asset</span>
                </router-link>
              </div>
            </div>
          </div>
        </section>

      </div>
    </div>

    <!-- 4. GLOBAL CLIENT FEEDBACK QR MODAL -->
    <div v-if="isClientFeedbackModalOpen" class="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div class="bg-white rounded-[40px] max-w-xl w-full p-8 md:p-10 shadow-2xl border border-slate-100 animate-fadeIn text-left space-y-8">
        <div class="flex justify-between items-center border-b pb-6">
          <div class="flex items-center gap-3">
            <div class="bg-[#0A1630] p-2 rounded-xl shadow-lg border-b-2 border-emerald-400">
              <QrCodeIcon class="h-5 w-5 text-emerald-400" />
            </div>
            <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">Client Feedback Questionnaire</h3>
          </div>
          <button @click="isClientFeedbackModalOpen = false" class="bg-slate-100 hover:bg-slate-200 text-slate-600 p-2.5 rounded-xl transition-all cursor-pointer">
            <XMarkIcon class="w-5 h-5" />
          </button>
        </div>

        <div class="flex flex-col items-center gap-6">
          <div class="bg-[#0A1630] p-4 rounded-[28px] shadow-xl border-4 border-emerald-400/20">
            <div class="bg-white p-3 rounded-2xl">
              <img v-if="globalClientFeedbackQrUrl" :src="globalClientFeedbackQrUrl" alt="Client Feedback QR" class="w-48 h-48" />
            </div>
          </div>
          <span class="text-[8px] font-black text-emerald-600 uppercase tracking-widest bg-emerald-50 px-3 py-1 rounded-full border border-emerald-100">
            Please scan the qr code to open the Client Feedback Questionnaire.
          </span>
        </div>

        <p class="text-xs text-slate-500 font-medium leading-relaxed text-center">
          Present this QR code or share the link with client representatives to evaluate our project service ratings and site performance.
        </p>

        <div class="flex flex-col gap-3">
          <button @click="shareGlobalFeedbackEmail" class="w-full bg-[#0A1630] text-white px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all flex items-center justify-center gap-2 shadow-md cursor-pointer border-none">
            <EnvelopeIcon class="w-4 h-4 text-emerald-400" /> <span>Email Questionnaire Link</span>
          </button>
          <button @click="copyGlobalFeedbackLink" class="w-full bg-white border border-slate-200 text-[#0A1630] px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 transition-all flex items-center justify-center gap-2 shadow-sm cursor-pointer">
            <ClipboardIcon class="w-4 h-4 text-emerald-400" /> <span>{{ copiedGlobalFeedbackText }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- 5. PROJECT DETAILS MODAL WITH CLIENT FEEDBACK HUB -->
    <div v-if="selectedProject" class="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div class="bg-white rounded-[40px] max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-slate-100 animate-fadeIn text-left">
        
        <!-- Modal Header -->
        <div class="bg-[#0A1630] p-8 md:p-10 text-white relative overflow-hidden">
          <div class="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
            <BuildingOffice2Icon class="h-28 w-28 text-white" />
          </div>
          
          <div class="relative z-10 flex justify-between items-start gap-4">
            <div>
              <span class="text-[8px] font-black px-3 py-1 bg-[#20FCF1] text-[#0A1630] rounded-md uppercase tracking-widest inline-block mb-3">
                {{ selectedProject.status || 'Active' }} Project Dossier
              </span>
              <h2 class="text-2xl md:text-3xl font-black uppercase tracking-tight leading-tight">{{ selectedProject.name }}</h2>
            </div>
            
            <button @click="selectedProject = null" class="bg-white/10 hover:bg-white/20 text-white p-3 rounded-2xl transition-all cursor-pointer">
              <XMarkIcon class="w-6 h-6" />
            </button>
          </div>
        </div>

        <div class="p-8 md:p-10 space-y-10">
          
          <!-- Key Project Details Grid -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div class="space-y-6">
              <div>
                <h4 class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Site Location</h4>
                <div class="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex items-center gap-3">
                  <MapPinIcon class="w-5 h-5 text-[#20FCF1] flex-shrink-0" />
                  <span class="text-xs font-bold text-[#0A1630] uppercase">{{ selectedProject.siteAddress || 'No Address Specified' }}</span>
                </div>
              </div>

              <div v-if="selectedProject.siteWhat3Words">
                <h4 class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">What3Words Reference</h4>
                <div class="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-between">
                  <span class="text-xs font-bold text-[#0A1630] font-mono">///{{ selectedProject.siteWhat3Words }}</span>
                  <a :href="`https://what3words.com/${selectedProject.siteWhat3Words}`" target="_blank" rel="noopener noreferrer" class="text-[9px] font-black text-[#20FCF1] hover:underline uppercase flex items-center gap-1">
                    <span>Open Map</span>
                    <ArrowTopRightOnSquareIcon class="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>
            </div>

            <div class="space-y-6">
              <div class="grid grid-cols-2 gap-4">
                <div class="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                  <h4 class="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Bridge Ref</h4>
                  <p class="text-xs font-bold text-[#0A1630] uppercase">{{ selectedProject.bridgeRef || 'N/A' }}</p>
                </div>
                <div class="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                  <h4 class="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Line / Track</h4>
                  <p class="text-xs font-bold text-[#0A1630] uppercase">{{ selectedProject.lineRef || 'N/A' }}</p>
                </div>
              </div>

              <div class="grid grid-cols-2 gap-4">
                <div class="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                  <h4 class="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Start Date</h4>
                  <p class="text-xs font-bold text-[#0A1630] uppercase">{{ selectedProject.startDate || 'N/A' }}</p>
                </div>
                <div class="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                  <h4 class="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">End Date</h4>
                  <p class="text-xs font-bold text-[#0A1630] uppercase">{{ selectedProject.endDate || 'N/A' }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Scope of Works -->
          <div v-if="selectedProject.description" class="space-y-2">
            <h4 class="text-[10px] font-black text-slate-400 uppercase tracking-widest">Scope of Works / Notes</h4>
            <div class="p-6 bg-slate-50 rounded-2xl border border-slate-100 text-xs text-slate-700 font-medium leading-relaxed whitespace-pre-wrap">
              {{ selectedProject.description }}
            </div>
          </div>

          <!-- Project Documents -->
          <div v-if="selectedProject.documents && selectedProject.documents.length > 0" class="space-y-4">
            <h4 class="text-[10px] font-black text-slate-400 uppercase tracking-widest">Attached Project Documents</h4>
            <div class="space-y-2">
              <a 
                v-for="(doc, idx) in selectedProject.documents" 
                :key="idx" 
                :href="doc.url" 
                target="_blank" 
                class="flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 rounded-2xl border border-slate-100 transition-colors"
              >
                <div class="flex items-center gap-3">
                  <DocumentTextIcon class="w-5 h-5 text-[#0A1630]" />
                  <span class="text-xs font-bold text-[#0A1630] uppercase">{{ doc.name }}</span>
                </div>
                <ArrowTopRightOnSquareIcon class="w-4 h-4 text-slate-400" />
              </a>
            </div>
          </div>

          <!-- CLIENT FEEDBACK QR CODE & CLOSEOUT HUB -->
          <div class="pt-8 border-t border-slate-100 space-y-6">
            <div class="flex items-center gap-3">
              <div class="bg-[#0A1630] p-2 rounded-xl shadow-lg border-b-2 border-emerald-400">
                <QrCodeIcon class="h-5 w-5 text-emerald-400" />
              </div>
              <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">Project Client Feedback & Closeout Hub</h3>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-8 items-center bg-slate-50 p-8 rounded-[32px] border border-slate-200">
              <div class="flex flex-col items-center gap-4">
                <div class="bg-[#0A1630] p-4 rounded-[28px] shadow-xl border-4 border-emerald-400/20">
                  <div class="bg-white p-3 rounded-2xl">
                    <img v-if="projectFeedbackQrUrl" :src="projectFeedbackQrUrl" alt="Project Feedback QR" class="w-32 h-32" />
                  </div>
                </div>
                <span class="text-[8px] font-black text-emerald-600 uppercase tracking-widest bg-emerald-50 px-3 py-1 rounded-full border border-emerald-100">
                  Client Feedback Questionnaire.
                </span>
              </div>

              <div class="space-y-4 text-center md:text-left">
                <h4 class="text-lg font-black text-[#0A1630] uppercase tracking-tight">Generate On-Site Closeout Review</h4>
                <p class="text-xs text-slate-500 font-medium leading-relaxed">
                  Present this QR code or transmit the link directly to the client site manager upon job completion to secure their evaluation and feedback.
                </p>

                <div class="flex flex-col gap-3">
                  <button @click="shareProjectFeedbackEmail" class="w-full bg-[#0A1630] text-white px-6 py-3.5 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all flex items-center justify-center gap-2 shadow-md cursor-pointer border-none">
                    <EnvelopeIcon class="w-4 h-4 text-emerald-400" /> <span>Email Project Feedback Link</span>
                  </button>
                  <button @click="copyProjectFeedbackLink" class="w-full bg-white border border-slate-200 text-[#0A1630] px-6 py-3.5 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 transition-all flex items-center justify-center gap-2 shadow-sm cursor-pointer">
                    <ClipboardIcon class="w-4 h-4 text-emerald-400" /> <span>{{ copiedProjectFeedbackText }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

        </div>

        <div class="bg-slate-50 px-8 py-6 border-t border-slate-100 flex justify-end">
          <button @click="selectedProject = null" class="px-8 py-3.5 bg-[#0A1630] text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-[#20FCF1] hover:text-[#0A1630] transition-all shadow-md cursor-pointer border-none">
            Close Dossier
          </button>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { db } from '@/firebaseConfig';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { useUserStore } from '@/stores/userStore';
import FormHeader from '@/components/FormHeader.vue';
import QRCode from 'qrcode';
import { 
  BuildingOffice2Icon, ArrowUturnLeftIcon, WrenchScrewdriverIcon, 
  MapPinIcon, DocumentTextIcon, 
  MagnifyingGlassIcon, EyeIcon, XMarkIcon, QrCodeIcon, 
  EnvelopeIcon, ClipboardIcon, ArrowTopRightOnSquareIcon,
  ClipboardDocumentCheckIcon, TableCellsIcon
} from '@heroicons/vue/24/outline';

const userStore = useUserStore();
const loading = ref(true);
const projects = ref([]);
const selectedProject = ref(null);
const projectFeedbackQrUrl = ref('');
const copiedProjectFeedbackText = ref('Copy Questionnaire Link');

// Global Client Feedback Modal state
const isClientFeedbackModalOpen = ref(false);
const globalClientFeedbackQrUrl = ref('');
const copiedGlobalFeedbackText = ref('Copy Questionnaire Link');

const activeRegistryTab = ref('projects'); // 'projects' or 'plant'
const searchQuery = ref('');
const selectedStatus = ref('All');
const plantStatusFilter = ref('all');
const statusTabs = ['All', 'Active', 'In Planning', 'Complete', 'On Hold'];

const defaultMasterRegister = [
  { assetId: 'TECS-EQ-001', description: 'Trimble S9 Total Station', serialNo: '38430004 / C016106', category: 'Survey', risk: 'Low', nextDue: '22-Dec-2026', status: 'In Service' },
  { assetId: 'TECS-EQ-002', description: 'Trimble S9 Total Station', serialNo: '38420076', category: 'Survey', risk: 'Low', nextDue: '03-Oct-2026', status: 'In Service' },
  { assetId: 'TECS-EQ-010', description: 'Live Line Tester', serialNo: '39P-1307/20P-0300 / 81790', category: 'Electrical / Safety', risk: 'High', nextDue: '06-Jan-2027', status: 'In Service' },
  { assetId: 'TECS-EQ-013', description: 'Therma 3 Thermometer', serialNo: 'D23180315 / 221-043', category: 'Survey', risk: 'Low', nextDue: 'N/A', status: 'Quarantined' },
  { assetId: 'TECS-EQ-122', description: 'HILTI SDSMAX Drill & Kit', serialNo: 'HILTISDS001', category: 'Power Tool', risk: 'High', nextDue: 'N/A', status: 'Quarantined' }
];

const fetchProjects = async () => {
  loading.value = true;
  try {
    const cid = userStore.clientId || userStore.userProfile?.clientId;
    if (!cid) {
      loading.value = false;
      return;
    }

    const q = query(
      collection(db, 'clients', cid, 'projects'),
      orderBy('name')
    );
    const snap = await getDocs(q);
    projects.value = snap.docs.map(d => ({
      id: d.id,
      ...d.data()
    }));
  } catch (err) {
    console.error("Projects Registry Sync Failure:", err);
  } finally {
    loading.value = false;
  }
};

const filteredProjects = computed(() => {
  return projects.value.filter(p => {
    const matchesStatus = selectedStatus.value === 'All' || p.status === selectedStatus.value;
    const queryStr = searchQuery.value.toLowerCase();
    const matchesSearch = !queryStr || 
      (p.name && p.name.toLowerCase().includes(queryStr)) ||
      (p.siteAddress && p.siteAddress.toLowerCase().includes(queryStr)) ||
      (p.bridgeRef && p.bridgeRef.toLowerCase().includes(queryStr)) ||
      (p.lineRef && p.lineRef.toLowerCase().includes(queryStr));
    return matchesStatus && matchesSearch;
  });
});

const filteredPlantAssets = computed(() => {
  return defaultMasterRegister.filter(a => {
    const matchesStatus = plantStatusFilter.value === 'all' || (a.status || '').toLowerCase() === plantStatusFilter.value.toLowerCase();
    const queryStr = searchQuery.value.toLowerCase();
    const matchesSearch = !queryStr ||
      (a.assetId && a.assetId.toLowerCase().includes(queryStr)) ||
      (a.description && a.description.toLowerCase().includes(queryStr)) ||
      (a.serialNo && a.serialNo.toLowerCase().includes(queryStr));
    return matchesStatus && matchesSearch;
  });
});

const openProjectDetails = async (project) => {
  selectedProject.value = project;
  try {
    const feedbackUrl = `${window.location.origin}/public/client-feedback/${userStore.clientId}?projectId=${project.id}`;
    projectFeedbackQrUrl.value = await QRCode.toDataURL(feedbackUrl, { width: 300, margin: 1 });
  } catch (err) {
    console.error("QR Generation Failure:", err);
  }
};

const openGlobalClientFeedbackModal = async () => {
  isClientFeedbackModalOpen.value = true;
  try {
    const cid = userStore.clientId || userStore.userProfile?.clientId;
    const feedbackUrl = `${window.location.origin}/public/client-feedback/${cid || 'general'}`;
    globalClientFeedbackQrUrl.value = await QRCode.toDataURL(feedbackUrl, { width: 300, margin: 1 });
  } catch (err) {
    console.error("QR Generation Failure:", err);
  }
};

const shareGlobalFeedbackEmail = () => {
  const cid = userStore.clientId || userStore.userProfile?.clientId;
  const feedbackUrl = `${window.location.origin}/public/client-feedback/${cid || 'general'}`;
  const subject = encodeURIComponent("Client Feedback Questionnaire");
  const body = encodeURIComponent("Hello,\n\nPlease take a moment to complete our client feedback questionnaire regarding our worksite performance using the link below:\n\n" + feedbackUrl + "\n\nThank you.");
  window.location.href = "mailto:?subject=" + subject + "&body=" + body;
};

const copyGlobalFeedbackLink = () => {
  const cid = userStore.clientId || userStore.userProfile?.clientId;
  const feedbackUrl = `${window.location.origin}/public/client-feedback/${cid || 'general'}`;
  navigator.clipboard.writeText(feedbackUrl).then(() => {
    copiedGlobalFeedbackText.value = 'Copied to Clipboard!';
    setTimeout(() => { copiedGlobalFeedbackText.value = 'Copy Questionnaire Link'; }, 2500);
  });
};

const shareProjectFeedbackEmail = () => {
  if (!selectedProject.value) return;
  const feedbackUrl = `${window.location.origin}/public/client-feedback/${userStore.clientId}?projectId=${selectedProject.value.id}`;
  const subject = encodeURIComponent("Client Feedback Questionnaire: " + selectedProject.value.name);
  const body = encodeURIComponent("Hello,\n\nPlease take a moment to complete our client feedback questionnaire regarding project " + selectedProject.value.name + " using the link below:\n\n" + feedbackUrl + "\n\nThank you.");
  window.location.href = "mailto:?subject=" + subject + "&body=" + body;
};

const copyProjectFeedbackLink = () => {
  if (!selectedProject.value) return;
  const feedbackUrl = `${window.location.origin}/public/client-feedback/${userStore.clientId}?projectId=${selectedProject.value.id}`;
  navigator.clipboard.writeText(feedbackUrl).then(() => {
    copiedProjectFeedbackText.value = 'Copied to Clipboard!';
    setTimeout(() => { copiedProjectFeedbackText.value = 'Copy Questionnaire Link'; }, 2500);
  });
};

onMounted(() => {
  if (userStore.isAuthReady) fetchProjects();
  else {
    const unwatch = userStore.$subscribe(() => {
      if (userStore.isAuthReady) { fetchProjects(); unwatch(); }
    });
  }
});
</script>

<style scoped>
.animate-fadeIn { animation: fadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
</style>